import * as React from 'react';

import cn from 'classnames';
import Slick from 'react-slick';

import GoogleMap from '../google-map-embed';

import { LandingPageState } from '../../types';
import * as urls from '../../urls';

import VenueCard from '../venue-card';
import StickyFooter from '../guide-sticky-footer';

const styles = require('./GuideLandingPage.scss');

const REGIONS = [
    {
        name: 'Mexico',
        link: '/guides/destination-weddings-mexico-resorts/',
        imageURL: require('./mexico.png'),
    },
    {
        name: 'Costa Rica',
        link: '/guides/destination-weddings-costa-rica-resorts/',
        imageURL: require('./costa-rica.png'),
    },
    {
        name: 'Dominican Republic',
        link: '/guides/destination-weddings-caribbean-island-resorts/',
        imageURL: require('./dominican.png'),
    },
    {
        name: 'Caribbean Islands',
        link: '/guides/destination-weddings-caribbean-island-resorts/',
        imageURL: require('./islands.png'),
    },
    {
        name: 'Jamaica',
        link: '/guides/destination-weddings-jamaica-resorts/',
        imageURL: require('./jamaica.jpg'),
    },
];

interface Props {
    data: LandingPageState;
    onVenueListingClick: (link: string, position: number) => void;
    onSeeAllButtonClick: (link: string) => void;
    onHeroPhotoClick: (link: string, photoOrder: number) => void;
    onLandingPageClick: (link: string, region: string) => void;
}

export const GuideLandingPage: React.FC<Props> = ({
    data,
    onVenueListingClick,
    onSeeAllButtonClick,
    onHeroPhotoClick,
    onLandingPageClick,
}) => {
    const recommendedVenues = data.recommendedVenues
        .filter(
            // Unlisted venues will be returned with only partial data so filter them out
            (item) => !!item && !!item.id && !!item.name && !!item.images && item.is_priced !== undefined
        )
        .map(({ id, name, images, is_priced }) => ({
            id,
            name,
            isPriced: is_priced,
            imageURL: images[0] ? images[0].storage_url + images[0].image : '',
        }));

    return (
        <div className={styles.className}>
            <div
                className={cn(styles.banner, styles.bannerFirst)}
                style={{
                    backgroundImage: 'url(' + data.hero_image_1_url + ')',
                }}
            >
                <h1 className={styles.bannerText}>{data.title_1}</h1>
            </div>

            <div className={styles.container}>
                <div className={styles.photoCredit}>
                    photo:{' '}
                    <a
                        href={data.hero_image_1_link}
                        onClick={(e: React.SyntheticEvent<HTMLAnchorElement>) => {
                            e.preventDefault();
                            onHeroPhotoClick(data.hero_image_1_link, 1);
                        }}
                        className={styles.accent}
                    >
                        {data.hero_image_1_credit}
                    </a>
                </div>
                <p className={styles.description}>{data.description_1}</p>
                <p className={styles.description}>
                    <a href={urls.getMarketAndRegionURL(data.market)} className={styles.exploreLink}>
                        EXPLORE {data.name} »
                    </a>
                </p>

                <h2 className={styles.headline}>Popular Resorts</h2>
                <div className={cn(styles.venueCardsWrapper, styles.hiddenMobile)}>
                    {recommendedVenues.map((venue, index) => (
                        <div key={venue.id} className={styles.venueCardWrapper}>
                            <VenueCard
                                onLinkClick={(link: string) => onVenueListingClick(link, index + 1)}
                                venueId={venue.id}
                                name={venue.name}
                                isPriced={!!venue.isPriced}
                                imageURL={venue.imageURL}
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div className={cn(styles.venueCardsWrapper, styles.slider, styles.hiddenDesktop)}>
                <Slick dots={true} centerMode={true} arrows={false} variableWidth={true} initialSlide={0}>
                    {recommendedVenues.map((venue, index) => (
                        <div key={venue.id} className={styles.venueCardWrapper}>
                            <VenueCard
                                onLinkClick={(link: string) => onVenueListingClick(link, index + 1)}
                                venueId={venue.id}
                                name={venue.name}
                                isPriced={!!venue.isPriced}
                                imageURL={venue.imageURL}
                            />
                        </div>
                    ))}
                </Slick>
            </div>
            <div className={styles.taCenter}>
                <a
                    href={urls.getMarketAndRegionURL(data.market)}
                    className={cn(styles.button, styles.viewAllButton)}
                    onClick={(e: React.SyntheticEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        onSeeAllButtonClick(urls.getMarketAndRegionURL(data.market));
                    }}
                >
                    View All
                </a>
            </div>
            <div
                className={cn(styles.banner, styles.bannerSecond)}
                style={{
                    backgroundImage: 'url(' + data.hero_image_2_url + ')',
                }}
            />
            <div className={cn(styles.container, styles.creditWrapper)}>
                <div className={styles.photoCredit}>
                    photo:{' '}
                    <a
                        href={data.hero_image_2_link}
                        onClick={(e: React.SyntheticEvent<HTMLAnchorElement>) => {
                            e.preventDefault();
                            onHeroPhotoClick(data.hero_image_2_link, 2);
                        }}
                        className={styles.accent}
                    >
                        {data.hero_image_2_credit}
                    </a>
                </div>
            </div>
            <div className={styles.container}>
                <p className={styles.description}>
                    <h2 className={cn(styles.headline, styles.headlineLeft)}>{data.title_2}</h2>
                    {data.description_2}
                </p>
            </div>

            <div className={styles.mapWrapper}>
                <GoogleMap name={(data.google_map_settings as any).q as string} width='100%' height='100%' />
            </div>

            <div className={styles.container}>
                <p className={styles.description}>{data.description_3}</p>
                <div className={styles.taCenter}>
                    <a
                        href={urls.getMarketAndRegionURL(data.market)}
                        className={cn(styles.button, styles.exploreButton)}
                        onClick={(e: React.SyntheticEvent<HTMLAnchorElement>) => {
                            e.preventDefault();
                            onSeeAllButtonClick(urls.getMarketAndRegionURL(data.market));
                        }}
                    >
                        Explore {data.name}
                    </a>
                </div>

                <h2 className={cn(styles.headline, styles.moreDestinationsHeadline)}>More Destinations</h2>
                <div className={styles.venueRegionsWrapper}>
                    {REGIONS.filter((region) => region.name !== data.region).map((region, index) => (
                        <div key={index} className={styles.venueRegionWrapper}>
                            <a
                                href={region.link}
                                className={styles.venueRegionLink}
                                onClick={(e: React.SyntheticEvent<HTMLAnchorElement>) => {
                                    e.preventDefault();
                                    onLandingPageClick(region.link, region.name);
                                }}
                            >
                                <div className={styles.venueRegion}>
                                    <img src={region.imageURL} alt={region.name} />
                                    <h3 className={styles.regionName}>{region.name}</h3>
                                </div>
                            </a>
                        </div>
                    ))}
                </div>
            </div>
            <div className={styles.stickyFooterWrapper}>
                <StickyFooter regionName={data.region} link={urls.getMarketAndRegionURL(data.market)} />
            </div>
        </div>
    );
};

export default GuideLandingPage;
